import React, { useState, useEffect, useCallback } from 'react';
import { 
  XMarkIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  CreditCardIcon,
  CalendarIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useClickOutside } from '../hooks/useClickOutside';

interface OrderItem {
  id: number;
  product_name: string;
  variant_color: string;
  variant_size: string;
  quantity: number;
  price: number;
  total: number;
  product?: {
    product_images?: string[];
    productImages?: Array<{ image_url: string }>;
  };
}

interface OrderDetail {
  id: number;
  order_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: string;
  total_amount: number;
  created_at: string;
  order_items: OrderItem[];
  shippingAddress?: {
    formatted_address: string;
    full_name: string;
    phone: string;
  };
  townshipDelivery?: {
    estimated_days: number;
    delivery_price: number;
  };
}

interface OrderDetailModalProps {
  orderNumber: string | null;
  onClose: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ orderNumber, onClose }) => {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const modalRef = useClickOutside(onClose);

  const fetchOrderDetails = useCallback(async () => {
    if (!orderNumber) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const orderData = await apiService.getUserOrderByNumber(orderNumber);
      setOrder(orderData);
    } catch (error) {
      setError('Failed to load order details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetails();
    }
  }, [orderNumber, fetchOrderDetails]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-50 border-green-200';
      case 'shipped': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'processing': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'pending': return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'cancelled': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircleIcon className="w-5 h-5" />;
      case 'shipped': return <TruckIcon className="w-5 h-5" />;
      case 'processing': return <ClockIcon className="w-5 h-5" />;
      case 'pending': return <ClockIcon className="w-5 h-5" />;
      case 'cancelled': return <XCircleIcon className="w-5 h-5" />;
      default: return <ClockIcon className="w-5 h-5" />;
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'approved':
      case 'paid': return 'text-green-600 bg-green-50 border-green-200';
      case 'pending_approval': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price) + ' Ks';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductImage = (item: OrderItem) => {
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59';
    
    // Check if product has product_images array (direct JSON array from database)
    if (item.product?.product_images && Array.isArray(item.product.product_images) && item.product.product_images.length > 0) {
      const imageUrl = item.product.product_images[0];
      return imageUrl.startsWith('http') ? imageUrl : `${baseUrl}/storage/${imageUrl}`;
    }
    
    // Fallback: Check if product has productImages array (nested structure)
    if (item.product?.productImages && item.product.productImages.length > 0) {
      const imageUrl = item.product.productImages[0].image_url;
      return imageUrl.startsWith('http') ? imageUrl : `${baseUrl}/storage/${imageUrl}`;
    }
    
    return '/placeholder-product.jpg';
  };

  if (!orderNumber) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <ShoppingBagIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Order Details</h2>
              {order && (
                <p className="text-gray-300 text-sm">#{order.order_number}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white transition-colors duration-200 p-2 hover:bg-white hover:bg-opacity-10 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircleIcon className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Order</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchOrderDetails}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : order ? (
            <div className="p-6 space-y-6">
              {/* Order Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Order Status</h3>
                    <CalendarIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    <span className="ml-2 capitalize">{order.status}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Ordered on {formatDate(order.created_at)}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Payment Status</h3>
                    <CreditCardIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${getPaymentStatusColor(order.payment_status)}`}>
                    <span className="capitalize">
                      {order.payment_status === 'pending_approval' ? 'Under Review' :
                       order.payment_status === 'approved' ? 'Approved' :
                       order.payment_status === 'rejected' ? 'Rejected' :
                       order.payment_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shipping Information */}
              {order.shippingAddress && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Shipping Address</h3>
                    <MapPinIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">{order.shippingAddress.full_name}</p>
                    <p className="text-gray-600">{order.shippingAddress.formatted_address}</p>
                    <p className="text-gray-600">{order.shippingAddress.phone}</p>
                  </div>
                  {order.townshipDelivery && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center text-sm text-gray-600">
                        <TruckIcon className="w-4 h-4 mr-2" />
                        <span>Estimated delivery: {order.townshipDelivery.estimated_days} days</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mt-1">
                        <span>Delivery fee: {formatPrice(order.townshipDelivery.delivery_price)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Order Items ({order.order_items?.length || 0})</h3>
                <div className="space-y-4">
                  {order.order_items?.map((item: OrderItem) => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={getProductImage(item)}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-product.jpg';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{item.product_name}</h4>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            {item.variant_color && (
                              <span>Color: {item.variant_color}</span>
                            )}
                            {item.variant_size && (
                              <span>Size: {item.variant_size}</span>
                            )}
                            <span>Qty: {item.quantity}</span>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-gray-600">
                              {formatPrice(item.price)} × {item.quantity}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {formatPrice(item.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900">
                      {formatPrice((order.total_amount || 0) - (order.townshipDelivery?.delivery_price || 0))}
                    </span>
                  </div>
                  {order.townshipDelivery && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivery Fee</span>
                      <span className="text-gray-900">{formatPrice(order.townshipDelivery.delivery_price)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold text-lg">
                      <span className="text-gray-900">Total</span>
                      <span className="text-gray-900">{formatPrice(order.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {order && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailModal;
